import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, RotateCcw, Home, Star, MoreHorizontal,
  Lock, Unlock, Shield, AlertTriangle, Download, Volume2,
  Settings, X, Plus, ChevronDown, Search, Bookmark,
  ExternalLink, Share, Copy, Printer, Eye, EyeOff, Globe,
  CheckCircle, BookOpen, Info, AlertCircle, ChevronRight,
  User, CreditCard, Calendar, MapPin, Phone
} from 'lucide-react';

const RealisticBrowserSimulator = ({
  scenario,
  onComplete,
  onExit,
  fullScreen = true,
  challenge,
  browserType = 'chrome'
}) => {
  const [currentUrl, setCurrentUrl] = useState('https://www.google.com');
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [history, setHistory] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [addressBarFocused, setAddressBarFocused] = useState(false);
  const [showDevTools, setShowDevTools] = useState(false);
  const [securityInfo, setSecurityInfo] = useState(null);
  const [popupBlocked, setPopupBlocked] = useState(0);
  const [certificateError, setCertificateError] = useState(null);
  const [downloadWarning, setDownloadWarning] = useState(null);
  const [activePopup, setActivePopup] = useState(null); // NEW
  const [feedbackModal, setFeedbackModal] = useState(null); // NEW for feedback
  const navigate = useNavigate();
  const addressBarRef = useRef(null);

  // Initialize default browser state
  useEffect(() => {
    const defaultTabs = [
      {
        id: 'tab-1',
        title: 'New Tab',
        url: 'chrome://newtab/',
        favicon: null,
        isActive: true,
        isLoading: false,
        content: <NewTabContent />
      }
    ];

    setTabs(defaultTabs);
    setActiveTabId('tab-1');

    setBookmarks([
      { name: 'Gmail', url: 'https://gmail.com', favicon: '📧' },
      { name: 'Company Portal', url: 'https://portal.company.com', favicon: '🏢' },
      { name: 'Bank Account', url: 'https://mybank.com', favicon: '🏦' },
      { name: 'News', url: 'https://news.com', favicon: '📰' },
      { name: 'Shopping', url: 'https://amazon.com', favicon: '🛒' }
    ]);

    // Initialize scenario if provided
    if (scenario) {
      initializeScenario();
    }
  }, [scenario]);

  const initializeScenario = () => {
    if (scenario.type === 'phishing_website') {
      setTimeout(() => {
        navigateToUrl(scenario.targetUrl);
      }, 2000);
    } else if (scenario.type === 'certificate_warning') {
      setTimeout(() => {
        setCertificateError({
          url: scenario.targetUrl,
          error: 'NET::ERR_CERT_AUTHORITY_INVALID',
          message: 'Your connection is not private'
        });
      }, 1500);
    } else if (scenario.type === 'malicious_download') {
      setTimeout(() => {
        setDownloadWarning({
          filename: scenario.filename || 'suspicious_file.exe',
          size: scenario.fileSize || '2.3 MB',
          source: scenario.sourceUrl || 'unknown-site.net'
        });
      }, 3000);
    } else if (scenario.type === 'phishing_popup') {
      setTimeout(() => {
        navigateToUrl(scenario.url);
        setActivePopup(scenario.popup);
      }, 2000);
    }
  };

  const handleActionComplete = (summary) => {
    setFeedbackModal({
      isCorrect: summary.correct,
      title: summary.correct ? 'Correct Action!' : 'Incorrect Action!',
      explanation: summary.feedback,
      indicators: summary.indicators || []
    });
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackModal(null);
    if (onComplete) onComplete(feedbackModal); // Pass the summary to the container
  };

  const handleLearnMore = () => {
    if (challenge?.metadata) {
      window.open(challenge.metadata, '_blank', 'noopener,noreferrer');
    } else {
      const arenaIdMap = {
        'phishing_website': 'phishing_social_engineering',
        'certificate_warning': 'secure_browsing',
        'malicious_download': 'malware_ransomware',
        'phishing_popup': 'phishing_social_engineering',
      };
      const arenaId = scenario?.arenaId || (scenario?.type ? arenaIdMap[scenario.type] : 'secure_browsing');
      navigate(`/learning/module/${arenaId}?topic=${arenaId}`);
    }
  };
  const createNewTab = (url = 'chrome://newtab/', title = 'New Tab') => {
    const newTab = {
      id: `tab-${Date.now()}`,
      title,
      url,
      favicon: getFavicon(url),
      isActive: false,
      isLoading: true,
      content: getPageContent(url)
    };

    setTabs(prev => [...prev, newTab]);
    switchToTab(newTab.id);

    // Simulate loading
    setTimeout(() => {
      setTabs(prev => prev.map(tab =>
        tab.id === newTab.id ? { ...tab, isLoading: false } : tab
      ));
    }, 1000);
  };

  const closeTab = (tabId) => {
    setTabs(prev => {
      const filtered = prev.filter(tab => tab.id !== tabId);
      if (filtered.length === 0) {
        const newTab = {
          id: `tab-${Date.now()}`,
          title: 'New Tab',
          url: 'chrome://newtab/',
          favicon: null,
          isActive: true,
          isLoading: false,
          content: <NewTabContent />
        };
        setActiveTabId(newTab.id);
        return [newTab];
      } else if (activeTabId === tabId) {
        const newActiveTab = filtered[filtered.length - 1];
        setActiveTabId(newActiveTab.id);
        setCurrentUrl(newActiveTab.url);
      }
      return filtered;
    });
  };

  const switchToTab = (tabId) => {
    setActiveTabId(tabId);
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      setCurrentUrl(tab.url);
      setTabs(prev => prev.map(t => ({ ...t, isActive: t.id === tabId })));
    }
  };

  const navigateToUrl = (url) => {
    setIsLoading(true);
    setCurrentUrl(url);

    const newTitle = getTitleFromUrl(url);
    setTabs(prev => prev.map(tab =>
      tab.id === activeTabId
        ? {
          ...tab,
          url,
          title: newTitle,
          isLoading: true,
          favicon: getFavicon(url),
          content: getPageContent(url)
        }
        : tab
    ));

    setHistory(prev => [{ url, title: newTitle, timestamp: new Date() }, ...prev.slice(0, 99)]);
    checkSecurityWarnings(url);

    setTimeout(() => {
      setIsLoading(false);
      setTabs(prev => prev.map(tab =>
        tab.id === activeTabId ? { ...tab, isLoading: false } : tab
      ));
    }, Math.random() * 2000 + 500);
  };

  const checkSecurityWarnings = (url) => {
    if (url.startsWith('http://')) {
      setSecurityInfo({
        type: 'insecure',
        message: 'This connection is not secure'
      });
    } else if (url.startsWith('https://')) {
      const suspiciousDomains = [
        'secure-bank-login.net',
        'paypal-verification.com',
        'microsoft-security.org',
        'amazon-deals.biz'
      ];

      const domain = new URL(url).hostname;
      if (suspiciousDomains.includes(domain)) {
        setSecurityInfo({
          type: 'warning',
          message: 'This site may be impersonating a legitimate service'
        });
      } else {
        setSecurityInfo({
          type: 'secure',
          message: 'Connection is secure'
        });
      }
    }
  };

  const getFavicon = (url) => {
    const faviconMap = {
      'https://www.google.com': '🔍',
      'https://gmail.com': '📧',
      'https://github.com': '🐙',
      'https://stackoverflow.com': '📚',
      'https://amazon.com': '🛒',
      'https://secure-bank-login.net': '🏦',
      'https://paypal-verification.com': '💳'
    };

    return faviconMap[url] || '🌐';
  };

  const getTitleFromUrl = (url) => {
    const titleMap = {
      'https://www.google.com': 'Google',
    };

    return titleMap[url] || new URL(url).hostname;
  };

  const getPageContent = (url) => {
    switch (url) {
      case 'chrome://newtab/':
        return <NewTabContent />;
      case 'https://secure-bank-login.net/login':
        return <PhishingBankPage onComplete={handleActionComplete} />;
      case 'https://paypal-verification.com/verify':
        return <PhishingPayPalPage onComplete={handleActionComplete} />;
      case 'https://microsoft-security.org/update':
        return <FakeMicrosoftPage onComplete={handleActionComplete} />;
      case 'https://free-antivirus-download.biz':
        return <MaliciousDownloadPage onDownload={() => setDownloadWarning({
          filename: 'FreeAntivirus_Setup.exe',
          size: '15.2 MB',
          source: 'free-antivirus-download.biz'
        })} />;
      default:
        return <LegitimateWebsite url={url} />;
    }
  };

  const activeTab = tabs.find(tab => tab.id === activeTabId);

  // ------------------------
  // RETURN JSX
  // ------------------------
  return (
    <div className={`${fullScreen ? 'fixed inset-0 z-[9998]' : 'relative w-full h-full'} bg-white overflow-hidden`}>
      {/* Browser Chrome */}
      <div className="flex flex-col h-full">
        {/* Title Bar */}
        <div className="flex items-center bg-gray-800 border-b border-gray-700 px-2 py-1 text-gray-200">
          <div className="flex items-center space-x-2 flex-1"></div>
          {fullScreen}
        </div>

        {/* Tab Bar */}
        <div className="flex items-end bg-gray-900 border-b border-gray-700 px-2 overflow-x-auto">
          <div className="flex min-w-max">
            {tabs.map(tab => (
              <div
                key={tab.id}
                className={`flex items-center px-3 sm:px-4 py-2 max-w-44 sm:max-w-56 cursor-pointer border-t border-l border-r ${tab.id === activeTabId
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
                  }`}
                style={{ borderRadius: '8px 8px 0 0' }}
                onClick={() => switchToTab(tab.id)}
              >
                <div className="flex items-center flex-1 min-w-0">
                  {tab.isLoading ? (
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                  ) : (
                    <span className="mr-2 text-sm">{tab.favicon || '🌐'}</span>
                  )}
                  <span className="truncate text-sm">{tab.title}</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    closeTab(tab.id);
                  }}
                  className="ml-2 p-1 hover:bg-gray-600 rounded"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => createNewTab()}
            className="ml-1 p-2 hover:bg-gray-700 rounded text-gray-300"
          >
            <Plus size={16} />
          </button>
        </div>

        {/* Navigation Bar */}
        <div className="flex items-center bg-gray-800 border-b border-gray-700 px-2 sm:px-3 py-2 space-x-1 sm:space-x-2 text-gray-300">
          <div className="hidden sm:flex items-center space-x-1">
            <button className="p-2 hover:bg-gray-700 rounded disabled:opacity-50" disabled>
              <ArrowLeft size={16} />
            </button>
            <button className="p-2 hover:bg-gray-700 rounded disabled:opacity-50" disabled>
              <ArrowRight size={16} />
            </button>
            <button className="p-2 hover:bg-gray-700 rounded" onClick={() => navigateToUrl(currentUrl)}>
              <RotateCcw size={16} />
            </button>
          </div>

          <div className="flex items-center flex-1 min-w-0 bg-gray-900 border border-gray-600 rounded-full px-3 sm:px-4 py-2 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
            <div className="flex items-center mr-2">
              {securityInfo?.type === 'secure' && <Lock size={16} className="text-green-600" />}
              {securityInfo?.type === 'insecure' && <AlertTriangle size={16} className="text-red-600" />}
              {securityInfo?.type === 'warning' && <AlertTriangle size={16} className="text-yellow-600" />}
            </div>
            <input
              ref={addressBarRef}
              type="text"
              value={currentUrl}
              onChange={(e) => setCurrentUrl(e.target.value)}
              onFocus={() => setAddressBarFocused(true)}
              onBlur={() => setAddressBarFocused(false)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  let url = currentUrl;
                  if (!url.startsWith('http://') && !url.startsWith('https://')) {
                    url = 'https://' + url;
                  }
                  navigateToUrl(url);
                  addressBarRef.current.blur();
                }
              }}
              className="flex-1 outline-none text-sm bg-transparent text-gray-200"
              placeholder="Search Google or type a URL"
            />
            {isLoading && (
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin ml-2"></div>
            )}
          </div>

          <div className="hidden sm:flex items-center space-x-1">
            <button className="p-2 hover:bg-gray-700 rounded"><Star size={16} /></button>
            <button className="p-2 hover:bg-gray-700 rounded"><Shield size={16} className="text-green-600" /></button>
            <button className="p-2 hover:bg-gray-700 rounded"><MoreHorizontal size={16} /></button>
          </div>

          <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
            <User size={16} className="text-white" />
          </div>
        </div>

        {/* Bookmarks */}
        <div className="hidden sm:flex items-center bg-gray-800 border-b border-gray-700 px-4 py-1 space-x-4 overflow-x-auto">
          {bookmarks.map((bookmark, index) => (
            <button
              key={index}
              onClick={() => navigateToUrl(bookmark.url)}
              className="flex items-center space-x-1 px-2 py-1 hover:bg-gray-200 rounded text-sm"
            >
              <span>{bookmark.favicon}</span>
              <span className="text-sm text-gray-300 hover:text-gray-800">{bookmark.name}</span>
            </button>
          ))}
        </div>

        {/* Security Warning */}
        {securityInfo?.type === 'warning' && (
          <div className="bg-yellow-100 border-b border-yellow-300 px-4 py-2">
            <div className="flex items-center space-x-2">
              <AlertTriangle size={16} className="text-yellow-600" />
              <span className="text-sm text-yellow-800">{securityInfo.message}</span>
              <button className="text-yellow-800 hover:text-yellow-900 text-sm font-medium">Learn more</button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">{activeTab && activeTab.content}</div>

        {/* Certificate Error Overlay */}
        {certificateError && (
          <div className="absolute inset-0 bg-white flex items-center justify-center">
            {/* ... existing cert warning UI ... */}
          </div>
        )}

        {/* Download Warning Overlay */}
        {downloadWarning && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            {/* ... existing download warning UI ... */}
          </div>
        )}

        {/* Phishing Popup Overlay */}
        {activePopup && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-lg font-bold mb-4">{scenario.title}</h2>
              <p className="text-gray-700 mb-4">{activePopup.message}</p>
              <div className="space-y-3">
                {activePopup.fields.map((field, idx) => (
                  <input
                    key={idx}
                    type={field === 'password' ? 'password' : 'text'}
                    placeholder={field}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                ))}
              </div>
              <div className="mt-6 space-y-2">
                {activePopup.actions.map((act, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      if (onComplete) onComplete(act);
                      setActivePopup(null);
                    }}
                    className={`w-full px-4 py-2 rounded font-medium ${act.correct
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                  >
                    {act.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FeedbackModal = ({ feedback, onClose, onLearnMore }) => {
  if (!feedback) return null;
  const { isCorrect, title, explanation, indicators } = feedback;

  return (
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-8 m-4 animate-fade-in-up">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            {isCorrect ? <CheckCircle size={28} className="text-green-600" /> : <AlertTriangle size={28} className="text-red-600" />}
            <h2 className={`text-2xl font-bold ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>{title}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>
        <div className="text-gray-700 space-y-4">
          <p className="text-base">{explanation}</p>
          {indicators && indicators.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h3 className="font-bold text-yellow-800 flex items-center">
                <AlertTriangle size={16} className="mr-2" />
                Key Indicators Found:
              </h3>
              <ul className="list-disc list-inside space-y-1.5 mt-2 text-gray-700 text-sm pl-2">
                {indicators.map((indicator, index) => <li key={index}>{indicator}</li>)}
              </ul>
            </div>
          )}
        </div>

        {/* Modal Footer with Buttons */}
        <div className="mt-8 flex justify-end items-center gap-4">
          <button
            onClick={onLearnMore}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
          >
            <BookOpen size={16} />
            Learn More
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// New Tab Content
const NewTabContent = () => {
  return (
    <div className="h-full bg-white flex flex-col items-center justify-center">
      <div className="max-w-2xl w-full px-8">
        <div className="text-center mb-12">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl text-white">G</span>
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="Search Google or type a URL"
              className="w-full px-6 py-4 text-lg border border-gray-300 rounded-full shadow-sm focus:outline-none focus:border-blue-500 focus:shadow-md"
            />
            <Search size={20} className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
            { name: 'Gmail', icon: '📧', color: 'bg-red-100' },
            { name: 'Drive', icon: '📁', color: 'bg-blue-100' },
            { name: 'YouTube', icon: '📺', color: 'bg-red-100' },
            { name: 'Maps', icon: '🗺️', color: 'bg-green-100' },
            { name: 'News', icon: '📰', color: 'bg-yellow-100' },
            { name: 'Translate', icon: '🔤', color: 'bg-blue-100' },
            { name: 'Photos', icon: '📷', color: 'bg-purple-100' },
            { name: 'Calendar', icon: '📅', color: 'bg-green-100' }
          ].map((app, index) => (
            <div key={index} className="flex flex-col items-center p-4 hover:bg-gray-50 rounded-lg cursor-pointer">
              <div className={`w-12 h-12 ${app.color} rounded-lg flex items-center justify-center mb-2`}>
                <span className="text-xl">{app.icon}</span>
              </div>
              <span className="text-sm text-gray-700">{app.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Phishing Bank Page
const PhishingBankPage = ({ onComplete }) => {
  const [formData, setFormData] = useState({
    accountNumber: '',
    password: '',
    ssn: '',
    pin: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onComplete({
      action: 'entered_credentials_on_phishing_site',
      correct: false,
      feedback: 'This was a phishing site! The correct action was to close the tab.',
      indicators: ["Suspicious URL (not the official bank domain)", "Requests excessive personal information (SSN, PIN)", "Uses urgent language to create panic"],
      data: formData
    });
  };

  return (
    <div className="h-full bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center p-8">
      <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl text-white">🏦</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">SecureBank Online</h2>
          <p className="text-gray-600">Secure Account Access</p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertTriangle size={20} className="text-red-600 mr-2" />
            <div>
              <p className="font-semibold text-red-800">Security Alert</p>
              <p className="text-sm text-red-600">Unusual activity detected. Please verify your identity immediately.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
            <input
              type="text"
              value={formData.accountNumber}
              onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              placeholder="Enter your account number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              placeholder="Enter your password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Social Security Number</label>
            <input
              type="text"
              value={formData.ssn}
              onChange={(e) => setFormData({ ...formData, ssn: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              placeholder="XXX-XX-XXXX"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">PIN</label>
            <input
              type="password"
              value={formData.pin}
              onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              placeholder="4-digit PIN"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-3 rounded font-medium hover:bg-blue-700 transition-colors"
          >
            Verify Account
          </button>
        </form>

        <p className="text-xs text-gray-500 mt-4 text-center">
          For your security, please verify all information immediately.
        </p>
      </div>
    </div>

  );
};

// Phishing PayPal Page
const PhishingPayPalPage = ({ onComplete }) => {
  return (
    <div className="h-full bg-gray-100 flex items-center justify-center p-8">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-lg w-full">
        <div className="flex items-center justify-center mb-6">
          <span className="text-3xl text-blue-600 font-bold">PayPal</span>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <h3 className="font-bold text-yellow-800 mb-2">Account Verification Required</h3>
          <p className="text-sm text-yellow-700">
            Your account has been limited due to suspicious activity. Verify your information within 24 hours to avoid permanent suspension.
          </p>
        </div>

        <form className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              placeholder="Enter password"
            />
          </div>

          <button
            onClick={() => onComplete({
              action: 'entered_paypal_credentials',
              correct: false,
              feedback: 'This was a phishing attempt! The correct action was to close the tab and report the email.',
              indicators: ["URL is not the official PayPal domain", "Urgent warning about account suspension", "Asks for login credentials via an email link"]
            })}
            className="w-full bg-blue-600 text-white py-3 rounded hover:bg-blue-700"
          >
            Verify Account
          </button>
        </form>
      </div>
    </div>
  );
};

// Fake Microsoft Page
const FakeMicrosoftPage = ({ onComplete }) => {
  return (
    <div className="h-full bg-blue-600 text-white flex items-center justify-center p-8">
      <div className="max-w-2xl text-center">
        <div className="mb-8">
          <span className="text-4xl font-bold">Microsoft</span>
        </div>

        <div className="bg-red-600 rounded-lg p-6 mb-8">
          <AlertTriangle size={48} className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">CRITICAL SECURITY UPDATE REQUIRED</h2>
          <p className="text-lg">
            Your Windows system has been compromised! Download the security patch immediately to protect your computer.
          </p>
        </div>

        <button
          onClick={() => onComplete({
            action: 'clicked_fake_security_update',
            correct: false,
            feedback: 'This was a fake Microsoft page! Microsoft never requires immediate downloads through browser pop-ups. The correct action was to close the tab.',
            indicators: ["Uses scare tactics (e.g., 'system compromised')", "Forces an immediate download", "Not on an official Microsoft domain"]
          })}
          className="bg-yellow-500 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-400 animate-pulse"
        >
          DOWNLOAD SECURITY PATCH NOW
        </button>

        <p className="text-sm mt-4 opacity-80">
          Warning: Failure to update within 1 hour may result in permanent data loss.
        </p>
      </div>
    </div>
  );
};

// Malicious Download Page
const MaliciousDownloadPage = ({ onDownload }) => {
  return (
    <div className="h-full bg-gradient-to-br from-green-500 to-blue-600 text-white flex items-center justify-center p-8">
      <div className="max-w-xl text-center">
        <div className="mb-8">
          <Shield size={64} className="mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-2">FREE Premium Antivirus!</h1>
          <p className="text-xl">Normally $99.99 - Today COMPLETELY FREE!</p>
        </div>

        <div className="bg-white/20 rounded-lg p-6 mb-8">
          <h3 className="text-2xl font-bold mb-4">✨ Premium Features Included:</h3>
          <ul className="text-left space-y-2">
            <li>🛡️ Real-time virus protection</li>
            <li>🔥 Advanced firewall</li>
            <li>🔐 Password manager</li>
            <li>🌐 Safe browsing protection</li>
            <li>📱 Mobile device security</li>
          </ul>
        </div>

        <div className="space-y-4">
          <button
            onClick={onDownload}
            className="bg-yellow-500 text-black px-12 py-4 rounded-lg text-2xl font-bold hover:bg-yellow-400 transform hover:scale-105 transition-all animate-pulse"
          >
            ⬇️ DOWNLOAD NOW - FREE!
          </button>

          <div className="flex items-center justify-center space-x-4 text-sm">
            <div className="flex items-center">
              <CheckCircle size={16} className="mr-1" />
              <span>100% Safe</span>
            </div>
            <div className="flex items-center">
              <CheckCircle size={16} className="mr-1" />
              <span>No Credit Card</span>
            </div>
            <div className="flex items-center">
              <CheckCircle size={16} className="mr-1" />
              <span>Instant Download</span>
            </div>
          </div>
        </div>

        <p className="text-xs mt-6 opacity-75">
          ⏰ Limited time offer! Download expires in 59 minutes.
        </p>
      </div>
    </div>
  );
};

// Legitimate Website Component
const LegitimateWebsite = ({ url }) => {
  return (
    <div className="h-full bg-white flex items-center justify-center p-8">
      <div className="text-center">
        <Globe size={64} className="mx-auto mb-4 text-gray-400" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Legitimate Website</h2>
        <p className="text-gray-600 mb-4">{url}</p>
        <p className="text-sm text-gray-500">
          This represents a legitimate, secure website with proper certificates and security measures.
        </p>

        <div className="mt-8 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-center">
            <CheckCircle size={20} className="text-green-600 mr-2" />
            <span className="text-green-800 font-medium">Secure Connection</span>
          </div>
          <p className="text-sm text-green-600 mt-1">
            HTTPS encryption protects your data
          </p>
        </div>
      </div>
    </div>
  );
};

export default RealisticBrowserSimulator;